/**
 * C3 长图/横宽「整图」看图台(任务详情页,posterKind=LONG_IMAGE 且整图已出时渲染)。
 *
 * 为什么不复用 AssetEditor:那是海报画布编辑器(logo/QR 锚点拖拽 + 滤镜 + i2i 改图),
 * 而长图的「改」本来就走分段面板的单块重出(LongImageSegmentsPanel),不是整图重画。
 *
 * 为什么不用缩略图网格:整图与逐块分段同列 = 把 1 件作品显示成 N 件(用户实锤反馈
 * 「详情图是分开的各张图片」),而且 aspect-[3/4] + object-cover 会把长图裁成中间一条。
 *
 * ═══ 2026-08-07 版面重构:从「一块卡」变成「看图台」════════════════════════════
 * 改前:`max-h-[70vh]` 的取景框 + `<img class="w-full">`,下面再纵向堆分段面板。
 * 测试机注探针实测(1500×8984 的真长图):
 *   · 1440×900  → 图渲成 1266×7582,窗口 628 → **一屏只见 8.3%**,整页 2.22 屏
 *   · 1920×1080 → 图渲成 1694×10146,窗口 754 → **7.2%**(屏幕越大越差)
 *   · 分段面板(长图真正的「画面调整」)在 y=1046 —— 首屏底下 146px,得先滚过取景框
 * 两条病:①页面和取景框**同时**可滚(滚轮到底在滚谁全凭指针位置);②操作台在下面,
 * 不是左右结构。
 *
 * 改后:整块吃满父级给的高度,页面自己不滚 —— 取景框是这一屏**唯一**的滚动区;
 * 分段面板挪到右侧 Dock(见 PosterTaskDetailPage),点某块这边跳过去。
 * 长图的比例决定了它不可能「一屏看全」(1:6),所以这里不做适屏,做的是**读得顺**。
 *
 * 皮肤:全语义 token;硬编码中文口径(V24 迁 i18n)。
 */
import { errorToast } from "@/shared/lib/apiErrors";
import { BookOpen, Download, Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { NominateToKbButton } from "@/entities/knowledge/ui/NominateToKbButton";
import { LongImageReader } from "@/entities/media-asset/ui/LongImageReader";
import { MediaPreviewLightbox } from "@/entities/media-asset/ui/MediaPreviewLightbox";
import { toast } from "@/shared/hooks/use-toast";
import { triggerDownload } from "@/shared/lib/download";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { posterService, type PosterAssetDto } from "../../api/posterService";

interface Props {
  taskId: number;
  /** 整图资产(assetMeta.segment 非 true 的那张,SUCCESS)。 */
  asset: PosterAssetDto;
  /** 是否有分段面板在右侧(有则提示「改某块去右边」)。 */
  hasSegments: boolean;
  /** LI-SCREEN-1:分段是「屏」(一键合屏成品)—— 指路话术按屏说,别再指「重出那一块」。 */
  screenSegments?: boolean;
  /**
   * 跳到整图的某个位置(0~1)。`nonce` 变化才触发 —— 同一块连点两次也要能再跳一次。
   * 位置由 `segmentOffsets` 按分段高度算,算不准时上层不会给(见那个模型的文件头)。
   */
  jump?: { ratio: number; nonce: number } | null;
  /**
   * 取景框**上沿**落在整图的哪个位置(0~1),给右侧 Dock 高亮「你在这一块」用。
   *
   * ★必须是 `scrollTop / scrollHeight`,和 `jump.ratio` **同一把尺**。
   *   真页面上抓到过:进度用 `scrollTop / (scrollHeight - clientHeight)`(滚动进度,0~1
   *   的另一种含义)、跳转用 `ratio * scrollHeight` —— 点「规格参数」跳对了位置,
   *   右边亮的却是「行动召唤」(0.762 被当成 0.855 去查表)。两把尺必须是同一把。
   * ★用上沿不用中线:短块(320/8984 = 3.6%,181px)比取景框(556)矮得多,
   *   按中线算会「跳到 A、亮的是 A 下面那块」。上沿 = 「我正在读的这一块」。
   */
  onTopRatio?: (ratio: number) => void;
  /**
   * 品牌落点示意(右侧「品牌」区选锚点时给)。
   *
   * ★尺寸按 ai 的常量换算,不另编一套:宽 = 整图宽 × 12%、四边留白 32 真实像素
   *   (`_LONGIMAGE_LOGO_RATIO` / `_LONGIMAGE_OVERLAY_PADDING`)。示意框和成品对不上
   *   就是在骗人,所以这两个数必须跟着 ai 走 —— 哪天 ai 改了,这里也要改。
   */
  /**
   * 头部动作插槽(2026-08-08 方案 C):详情页把「品牌」浮层塞在这儿。
   *
   * ★品牌是**整张图**的属性,和「下载整图 / 入库」同级 —— 它此前挂在右侧 Dock 里,
   *   和分段列表并排,两块都在回答「怎么给这张图加 logo」,挤的是概念不是像素。
   */
  headerExtra?: ReactNode;
}

/** 取景档:适宽 = 图宽铺满取景框(纵向连读);100% = 原始像素,横向可拖。 */
type Zoom = "fit" | "actual";

export function LongImageResultPanel({
  taskId,
  asset,
  hasSegments,
  screenSegments = false,
  jump,
  onTopRatio,
  headerExtra,
}: Props) {
  const [downloading, setDownloading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [zoom, setZoom] = useState<Zoom>("fit");
  const w = asset.actualWidth ?? 0;
  const h = asset.actualHeight ?? 0;
  // 取景用(纯渲染判断,不对外做「这是竖长/横宽」的断言):超长图纵向连读,其余整帧直出
  const isTall = w > 0 && h / w >= 1.6;
  const sizeLabel = w && h ? `${w}×${h}` : null;

  // ── D4:长度线索(W2 拍板方案 B)────────────────────────────────────────
  const scrollRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ top: 0, scroll: 0, client: 0 });
  const topRatioCb = useRef(onTopRatio);
  topRatioCb.current = onTopRatio;

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setView({ top: el.scrollTop, scroll: el.scrollHeight, client: el.clientHeight });
    // 与 jump.ratio 同一把尺:整图坐标系,不是滚动进度(见 onTopRatio 的注释)
    topRatioCb.current?.(el.scrollHeight > 0 ? el.scrollTop / el.scrollHeight : 0);
  }, []);
  // 图是 lazy 加载的:挂载那一刻 scrollHeight 还没有值,必须在 img onLoad 后再量一次。
  useLayoutEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // ── 跳到某一块 ────────────────────────────────────────────────────────
  // ★按 scrollHeight 算绝对位置,而不是按原始像素 —— 适宽档下图被缩到框宽,
  //   两者不是一个尺度(阅读器那边踩过同一个坑:拿 9727px 报屏数把 8 屏说成 13 屏)。
  const jumpNonce = jump?.nonce ?? null;
  const jumpRatio = jump?.ratio ?? null;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || jumpNonce == null || jumpRatio == null) return;
    el.scrollTo({ top: jumpRatio * el.scrollHeight, behavior: "smooth" });
  }, [jumpNonce, jumpRatio]);

  const overflows = view.scroll > view.client + 1;
  const seenRatio = overflows ? view.client / view.scroll : 1;
  const progress = overflows ? Math.min(1, view.top / (view.scroll - view.client)) : 0;
  const screens = overflows ? Math.ceil(view.scroll / view.client) : 1;
  // ★按**滚动进度**分屏,不按 floor(top/client)+1:最后一屏通常是半屏(4869/504=9.66),
  //   按整屏取整会让「已经滚到底了」显示成「第 9 / 10 屏」—— 读起来像下面还有,正好把
  //   这个组件要消灭的那个误解又造回来。进度映射保证 top=0→1、到底→screens。
  const currentScreen = overflows ? Math.min(screens, Math.round(progress * (screens - 1)) + 1) : 1;
  const atBottom = overflows && view.top + view.client >= view.scroll - 2;

  async function handleDownload() {
    setDownloading(true);
    try {
      const url = await posterService.downloadAsset(asset.id);
      // 长图产物是 JPG(ai _jpeg_bytes),按 DTO mime 派生扩展名
      const ext = asset.mime === "image/jpeg" ? "jpg" : "png";
      triggerDownload(url, `longimage-${taskId}.${ext}`);
    } catch (e) {
      toast(errorToast(e, { fallbackTitle: "下载失败" }));
    } finally {
      setDownloading(false);
    }
  }

  const zoomBtn =
    "rounded px-2 py-0.5 text-2xs transition-colors hover:bg-muted disabled:opacity-40";

  return (
    <Card className="flex h-full min-h-0 flex-col">
      <CardHeader className="shrink-0 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            成品整图
            {/* 只标真实像素:竖长/横宽是 canvas.direction 决定的,而 direction 不在资产 DTO 里 ——
                按像素比例反推会说谎(区块少的竖长图如 750×662 会被标成「横宽」)。 */}
            {sizeLabel && (
              <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-2xs font-medium text-muted-foreground">
                {sizeLabel}
              </span>
            )}
            {/* 取景档只在真的超长时才有意义(横宽整帧直出,没有可切的东西) */}
            {isTall && (
              <span className="ml-1 inline-flex items-center gap-0.5 rounded-full border border-border/60 p-0.5">
                <button
                  type="button"
                  className={cn(zoomBtn, zoom === "fit" && "bg-primary/10 text-primary")}
                  aria-pressed={zoom === "fit"}
                  onClick={() => setZoom("fit")}
                >
                  适宽
                </button>
                <button
                  type="button"
                  className={cn(zoomBtn, zoom === "actual" && "bg-primary/10 text-primary")}
                  aria-pressed={zoom === "actual"}
                  disabled={!w}
                  title={w ? `原始 ${w}px 宽` : undefined}
                  onClick={() => setZoom("actual")}
                >
                  100%
                </button>
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {headerExtra}
            <NominateToKbButton taskId={taskId} asset={asset} />
            <Button size="sm" variant="outline" disabled={downloading} onClick={handleDownload}>
              {downloading ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="mr-1 h-3.5 w-3.5" />
              )}
              下载整图
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col gap-2">
        {/* ★定位上下文在**滚动容器之外** —— 长度条/渐隐/屏数必须钉在取景框上,
            放进滚动容器里会跟着内容一起滚走。 */}
        <div className="relative min-h-0 flex-1">
          <div
            ref={scrollRef}
            onScroll={measure}
            data-longimage-stage-scroll
            /* ★高度来自父级(h-full + flex-1),不再是 `max-h-[70vh]`。
               70vh 是按视口比例切的一刀,和「这张图有多高」无关,而且它让**页面**也能滚 ——
               一屏两个滚动区,滚轮到底在滚谁全凭指针在哪。 */
            className={cn(
              "h-full rounded-lg border border-border/60 bg-muted/30",
              isTall ? "overflow-auto" : "flex items-center justify-center overflow-hidden"
            )}
          >
            {asset.baseUrl ? (
              /* BP-01:面板内保留缩览,但必须能点开读 —— 缩览是索引不是阅读态。
                 竖长走长图阅读器(通用灯箱 contain 只会把 9727px 缩得更小),横宽走通用灯箱。 */
              <button
                type="button"
                onClick={() => setViewerOpen(true)}
                className={cn("block cursor-zoom-in", zoom === "actual" ? "w-max" : "w-full")}
                aria-label={`看大图:长图成品整图 ${sizeLabel ?? ""}`}
                data-testid="longimage-open-viewer"
              >
                <img
                  src={asset.baseUrl}
                  alt={`长图成品整图 ${sizeLabel ?? ""}`}
                  className={cn(
                    zoom === "actual" ? "block max-w-none" : "block w-full",
                    // 横宽:整帧塞进框里(contain),不撑满也不裁
                    !isTall && "max-h-full w-auto object-contain"
                  )}
                  style={zoom === "actual" && w ? { width: `${w}px` } : undefined}
                  loading="lazy"
                  decoding="async"
                  onLoad={measure}
                />
              </button>
            ) : (
              <div className="flex h-40 items-center justify-center text-xs text-muted-foreground">
                整图正在交付…
              </div>
            )}
          </div>

          {/* ── D4 长度线索三件套(只在真的溢出时出现)──────────────────────
              走查实测:750×3302 在 1108×504 的容器里渲成 4869px 高 → 一屏只看见
              10.4%,而容器既没有滚动条、没有渐隐、也没有「共几屏」,页面本身又在滚
              —— **第一眼看上去就是一张横图**。逃生口(点开进阅读器)是有的,但
              默认形态先给了错印象。这里补的就是那几个缺失的信号,取景一点没动。
              ★不设 overscroll-behavior:contain —— 内层滚到底后要把滚动**交还页面**,
              那是浏览器默认行为,锁住反而变成滚不动。 */}
          {overflows && (
            <>
              {/* 渐隐:说明「内容被切断了」,不是画到这就完了;滚到底自然消失 */}
              <div
                aria-hidden
                className={`pointer-events-none absolute inset-x-0 bottom-0 h-16 rounded-b-lg bg-gradient-to-b from-transparent to-background transition-opacity duration-200 ${
                  atBottom ? "opacity-0" : "opacity-100"
                }`}
              />
              {/* 长度条:常显(不是 hover 才出)。滑块长度 = 当前可见占全长的比例 ——
                  一眼看出「下面还有九屏」,这正是原来完全缺失的那个量。 */}
              <div
                aria-hidden
                className="pointer-events-none absolute bottom-2 right-2 top-2 w-1.5 rounded-full bg-foreground/10"
              >
                <div
                  className="absolute inset-x-0 rounded-full bg-primary/70"
                  style={{
                    height: `${Math.max(6, seenRatio * 100)}%`,
                    top: `${progress * (100 - Math.max(6, seenRatio * 100))}%`,
                  }}
                />
              </div>
              <div
                className="pointer-events-none absolute right-5 top-2 rounded-full bg-foreground/70 px-2 py-0.5 font-mono text-2xs text-background"
                data-testid="longimage-screen-counter"
              >
                {/* 1006:原写「第 N / M 页」,M 是按取景框高度切的滚动页数,不是设计屏数 ——
                    2 屏的长图在小窗口里显示「第 1 / 5 页」,被读成「这图有 5 屏」。改报浏览进度。 */}
                已浏览 {Math.round((screens > 1 ? (currentScreen - 1) / (screens - 1) : 1) * 100)}%
              </div>
              {/* 常驻通读入口:此前只有「图本身可点」这一个隐性入口,得先猜到才用得上。
                  ★它现在是**专注模式**,不再是唯一能读的地方 —— 取景框自己已经能读了。 */}
              <Button
                size="sm"
                className="absolute bottom-3 right-5 gap-1.5 shadow-md"
                onClick={() => setViewerOpen(true)}
                data-testid="longimage-read-cta"
              >
                <BookOpen className="h-3.5 w-3.5" />
                逐页通读
              </Button>
            </>
          )}
        </div>
        <p className="shrink-0 text-xs text-muted-foreground">
          {isTall ? "上下滚动连读,点开进入专注模式(可切 100% 原始像素);" : "点开看大图;"}
          下载得到的是原始像素整图。
          {hasSegments &&
            (screenSegments
              ? // 合屏成品:单块「调整/重出」对它是闸掉的,别指一条走不通的路(0901 真机走查)
                "想换某一屏的画法:右侧分段列表「重画这一屏」(一笔出图积分);改文字回搭建器改稿。"
              : "想改某一块:右侧「调整」改画面(免费重拼)、「重出」重画那一块(一笔出图积分)。")}
        </p>
      </CardContent>
      {asset.baseUrl &&
        (isTall ? (
          <LongImageReader
            open={viewerOpen}
            onOpenChange={setViewerOpen}
            url={asset.baseUrl}
            title="长图成品整图" /* 尺寸由阅读器自己渲染,标题别再重复一遍 */
            /* D-01:1280 缩览面板里已经下载过 —— 原图在路上时先拿它铺满,零额外流量 */
            placeholderUrl={asset.thumbUrl ?? null}
            width={w || null}
            height={h || null}
            onDownload={handleDownload}
            downloading={downloading}
          />
        ) : (
          <MediaPreviewLightbox
            open={viewerOpen}
            onOpenChange={setViewerOpen}
            items={[{ url: asset.baseUrl, kind: "image", title: "成品整图" }]}
            index={0}
            onIndexChange={() => {}}
            onDownload={handleDownload}
          />
        ))}
    </Card>
  );
}
